// ============================================================================
// Shared listings store — persists to localStorage
// ============================================================================

export type ListingStatus = 'active' | 'missing' | 'pending' | 'error'

export interface TrackedListing {
  asin: string
  title: string
  status: Record<string, ListingStatus>
  addedAt: string // ISO date string
}

const STORAGE_KEY = 'aplus-tracked-listings'

function loadListings(): TrackedListing[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (raw) return JSON.parse(raw)
  } catch {}
  return []
}

function saveListings(listings: TrackedListing[]) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(listings))
}

export function getListings(): TrackedListing[] {
  return loadListings()
}

export function addListing(asin: string, title: string): TrackedListing {
  const listings = loadListings()
  // Don't add duplicates
  const existing = listings.find((l) => l.asin === asin)
  if (existing) return existing

  const pending: Record<string, ListingStatus> = {}
  const codes = ['US', 'CA', 'MX', 'UK', 'DE', 'FR', 'IT', 'ES', 'JP', 'AU', 'IN']
  codes.forEach((c) => (pending[c] = 'pending'))

  const newListing: TrackedListing = {
    asin,
    title,
    status: pending,
    addedAt: new Date().toISOString(),
  }

  const updated = [newListing, ...listings]
  saveListings(updated)
  return newListing
}

export function removeListing(asin: string) {
  const listings = loadListings().filter((l) => l.asin !== asin)
  saveListings(listings)
}

export function formatAddedAt(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime()
  const mins = Math.floor(diff / 60000)
  if (mins < 1) return 'Just now'
  if (mins < 60) return `${mins}m ago`
  const hours = Math.floor(mins / 60)
  if (hours < 24) return `${hours}h ago`
  const days = Math.floor(hours / 24)
  if (days === 1) return '1 day ago'
  if (days < 7) return `${days} days ago`
  if (days < 14) return '1 week ago'
  const weeks = Math.floor(days / 7)
  return `${weeks} weeks ago`
}
